import { useEffect, useState } from "react";
import { FaAngleRight, FaEye } from "react-icons/fa6";
import {
  MdOutlineKeyboardBackspace,
  MdOutlineLocalPhone,
  MdOutlineStarOutline,
} from "react-icons/md";
import { useNavigate, useParams } from "react-router-dom";
import LoadingSpinner from "../../../../components/elements/LoadingSpinner";
import { CiMail } from "react-icons/ci";
import { GoLocation } from "react-icons/go";
import { FaBalanceScale } from "react-icons/fa";
import { BiDish } from "react-icons/bi";
import { HiPencil } from "react-icons/hi";
import { TbMoneybag } from "react-icons/tb";
import { useAppDispatch, useAppSelector } from "../../../../redux/hooks";
import { RootState } from "../../../../redux/store";
import {
  getRestaurantDetails,
  getRestaurantDocuments,
  getRestaurantBankInfo,
  clearCurrentRestaurant,
} from "../../../../redux/restaurants/restaurantsSlice";

const SummaryCard = ({
  vendor,
  title,
  icon,
  onClick,
}: {
  vendor: number;
  title: string;
  icon: JSX.Element;
  onClick?: () => void;
}) => {
  return (
    <div
      className={`border rounded-md p-4 my-3 w-[207px] ${
        onClick ? "cursor-pointer" : ""
      }`}
      onClick={onClick}
    >
      <h4 className="my-5">{title}</h4>
      <div className="flex items-center justify-between gap-2">
        <p className="text-4xl">{vendor}</p>
        <span className="border rounded-full p-2 flex text-secondary bg-[#008B501F]">
          {icon}
        </span>
      </div>
    </div>
  );
};

const DocumentCard = ({
  title,
  url,
  status,
}: {
  title: string;
  url: string | null;
  status: string;
}) => {
  return (
    <div className="border rounded-md p-4 my-3 h-[162px] w-[156px]">
      <h4 className="my-5">{title}</h4>
      <div className="flex flex-col gap-2">
        <span
          className={`text-xs px-2 py-1 rounded ${
            status === "approved"
              ? "bg-green-100 text-green-800"
              : status === "rejected"
              ? "bg-red-100 text-red-800"
              : "bg-yellow-100 text-yellow-800"
          }`}
        >
          {status}
        </span>
        {url ? (
          <a
            href={url}
            target="_blank"
            rel="noopener noreferrer"
            className="flex items-center gap-2 text-secondary underline"
          >
            <p className="text-xs font-semibold">Preview</p> <FaEye />
          </a>
        ) : (
          <p className="text-xs text-gray-500">No document uploaded</p>
        )}
      </div>
    </div>
  );
};

const VendorDetails = () => {
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const params = useParams();

  const {
    currentRestaurant,
    currentRestaurantDocuments,
    currentRestaurantBankInfo,
    detailsStatus,
    documentsStatus,
    bankInfoStatus,
    error,
  } = useAppSelector((state: RootState) => state.restaurants);

  useEffect(() => {
    if (params.id) {
      // Clear previous data when navigating to a new restaurant
      dispatch(clearCurrentRestaurant());
      // Fetch all restaurant data
      dispatch(getRestaurantDetails(params.id));
      dispatch(getRestaurantDocuments(params.id));
      dispatch(getRestaurantBankInfo(params.id));
    }
  }, [dispatch, params.id]);

  const isLoading = detailsStatus === "loading";

  return (
    <section className="font-mont">
      <div className="flex items-start gap-4">
        <button
          className="cursor-pointer p-2 rounded-full bg-[#008B5014]"
          onClick={() => navigate(-1)}
        >
          <MdOutlineKeyboardBackspace className="w-6 h-6" />
        </button>
        <article>
          <h1 className="text-2xl font-medium mb-2 inline-flex items-center gap-3">
            <span className="font-semibold">Restaurant</span> <FaAngleRight />
            <span className="font-semibold">
              {isLoading ? "..." : currentRestaurant?.restaurantInfo.name}
            </span>
          </h1>
          <p className="text-sm">
            Overview of FoodBank Restaurant platform operations and key metrics.
          </p>
        </article>
      </div>
      <section className="my-5">
        {isLoading ? (
          <div className="flex items-center justify-center h-64">
            <LoadingSpinner />
          </div>
        ) : error ? (
          <div className="flex items-center justify-center h-64">
            <p className="text-red-500">
              Error loading restaurant details: {error}
            </p>
          </div>
        ) : currentRestaurant ? (
          <>
            {/* Banner section */}
            <section className="relative">
              <article className="bg-orange100 text-white p-10 rounded-t-xl">
                <p className="text-5xl font-semibold text-center">
                  {currentRestaurant.restaurantInfo.name}
                </p>
                <div className="absolute top-7 left-4 w-16 h-16 bg-white rounded-full flex items-center justify-center">
                  <span className="text-2xl font-bold text-orange100">
                    {currentRestaurant.restaurantInfo.name.charAt(0)}
                  </span>
                </div>
              </article>
              <article className="flex items-center flex-col text-xs ml-10">
                <div>
                  <ul className="flex items-center gap-5 my-2">
                    <li className="flex items-center gap-1 font-medium text-gray-500">
                      <CiMail />
                      {currentRestaurant.restaurantInfo.email}
                    </li>
                    <li className="flex items-center gap-1 font-medium text-gray-500">
                      <span className="text-secondary">Manager:</span>
                      {currentRestaurant.restaurantInfo.managerName}
                    </li>
                    <li className="flex items-center gap-1 font-medium text-gray-500">
                      <span className="text-secondary">Type:</span>
                      {currentRestaurant.restaurantInfo.type}
                    </li>
                  </ul>
                  <ul className="flex items-center gap-5">
                    <li className="text-secondary">
                      Rating: {currentRestaurant.restaurantInfo.rating}/5
                    </li>
                    <li
                      className={
                        currentRestaurant.restaurantInfo.isOpen
                          ? "bg-[#008B501F] py-1 px-3 rounded-md font-semibold text-secondary"
                          : "text-red-500 py-1 px-3 rounded-md font-semibold bg-[#EA19191F]"
                      }
                    >
                      {currentRestaurant.restaurantInfo.isOpen
                        ? "Open"
                        : "Closed"}
                    </li>
                    <li className="text-gray-500">
                      Free Delivery:{" "}
                      {currentRestaurant.restaurantInfo.freeDelivery
                        ? "Yes"
                        : "No"}
                    </li>
                  </ul>
                </div>
              </article>
            </section>
            {/* Summary Section */}
            <section className="mt-5 border rounded-md p-5">
              <section>
                <h3 className="font-semibold text-lg">Summary</h3>
                <div className="flex items-center justify-between gap-4">
                  <SummaryCard
                    vendor={currentRestaurant.counts.totalCategories}
                    title="Total Categories"
                    icon={<FaBalanceScale className="w-6 h-6" />}
                    onClick={undefined}
                  />
                  <SummaryCard
                    vendor={currentRestaurant.counts.totalMeals}
                    title="Total Meals"
                    icon={<BiDish className="w-6 h-6" />}
                    onClick={() =>
                      navigate(
                        `/dashboard/restaurants/operations/vendor/${currentRestaurant.restaurantInfo.id}/meals`
                      )
                    }
                  />
                  <SummaryCard
                    vendor={currentRestaurant.counts.totalOrders}
                    title="Total Orders"
                    icon={
                      <span className="border border-secondary rounded-full p-1">
                        <HiPencil className="w-4 h-4" />
                      </span>
                    }
                    onClick={() =>
                      navigate(
                        `/dashboard/restaurants/operations/vendor/${currentRestaurant.restaurantInfo.id}/orders`
                      )
                    }
                  />
                  <SummaryCard
                    vendor={currentRestaurant.counts.balance}
                    title="Current Balance"
                    icon={<TbMoneybag className="w-6 h-6" />}
                    onClick={undefined}
                  />
                  <SummaryCard
                    vendor={currentRestaurant.counts.totalReviews}
                    title="Total Reviews"
                    icon={<MdOutlineStarOutline className="w-6 h-6" />}
                    onClick={undefined}
                  />
                </div>
              </section>
              {/* Documents section */}
              <section>
                <h3 className="text-lg font-semibold">Documents</h3>
                {documentsStatus === "loading" ? (
                  <div className="flex justify-center p-4">
                    <LoadingSpinner />
                  </div>
                ) : currentRestaurantDocuments &&
                  currentRestaurantDocuments.documents ? (
                  <article className="flex items-center gap-4">
                    <DocumentCard
                      title="CAC Certificate"
                      url={
                        currentRestaurantDocuments.documents
                          .cacRegistrationCertificate?.url || null
                      }
                      status={
                        currentRestaurantDocuments.documents
                          .cacRegistrationCertificate?.status || "pending"
                      }
                    />
                    <DocumentCard
                      title="Food License"
                      url={
                        currentRestaurantDocuments.documents.foodLicense?.url ||
                        null
                      }
                      status={
                        currentRestaurantDocuments.documents.foodLicense
                          ?.status || "pending"
                      }
                    />
                    <DocumentCard
                      title="Tax ID Number"
                      url={
                        currentRestaurantDocuments.documents
                          .taxIdentificationNumber?.url || null
                      }
                      status={
                        currentRestaurantDocuments.documents
                          .taxIdentificationNumber?.status || "pending"
                      }
                    />
                  </article>
                ) : (
                  <p className="text-gray-500">
                    No document information available.
                  </p>
                )}
              </section>
              {/* Account section */}
              <section>
                <h3 className="text-lg font-semibold">Account Information</h3>
                {bankInfoStatus === "loading" ? (
                  <div className="flex justify-center p-4">
                    <LoadingSpinner />
                  </div>
                ) : currentRestaurantBankInfo &&
                  currentRestaurantBankInfo.bankAccounts.length > 0 ? (
                  <article className="my-4 w-3/5">
                    {currentRestaurantBankInfo.bankAccounts.map(
                      (account, index) => (
                        <div key={index} className="border rounded-md p-4 mb-4">
                          <div className="flex items-center gap-5 justify-between my-2">
                            <label htmlFor={`accountNumber${index}`}>
                              Account Number
                            </label>
                            <input
                              type="text"
                              id={`accountNumber${index}`}
                              value={account.accountNumber || "N/A"}
                              readOnly
                              className="border rounded-md p-3 w-2/3"
                            />
                          </div>
                          <div className="flex items-center gap-5 justify-between my-2">
                            <label htmlFor={`bankName${index}`}>
                              Bank Name
                            </label>
                            <input
                              type="text"
                              id={`bankName${index}`}
                              value={account.bankName || "N/A"}
                              readOnly
                              className="border rounded-md p-3 w-2/3"
                            />
                          </div>
                        </div>
                      )
                    )}
                    {/* <p className="text-xs text-red-400">
                      Please Note that all Earnings are received by above
                      account details to change{" "}
                      <span className="text-secondary font-medium cursor-pointer">
                        Contact Support
                      </span>
                    </p> */}
                  </article>
                ) : (
                  <div className="my-4 p-4 border rounded-md bg-gray-50">
                    <p className="text-gray-500">
                      No bank accounts registered for this restaurant.
                    </p>
                    <p className="text-xs text-gray-400 mt-2">
                      Total accounts:{" "}
                      {currentRestaurantBankInfo
                        ? currentRestaurantBankInfo.totalAccounts
                        : 0}
                    </p>
                  </div>
                )}
              </section>
              {/* Promotion section */}
              <section>
                <h3 className="text-lg font-semibold">Promotions</h3>
                <article className="my-4 flex items-center justify-between border rounded-md gap-4 p-5">
                  <div>
                    <p className="text-secondary my-2 font-semibold">
                      30% Off Weekend Special
                    </p>
                    <p className="text-sm my-2 font-medium">
                      Get 30% off on all rice dishes this weekend
                    </p>
                    <p className="text-sm my-2 font-medium text-orange100">
                      Valid until: Jan 28, 2025
                    </p>
                  </div>
                  <div className="flex items-center gap-5">
                    <button
                      type="button"
                      className="text-secondary bg-[#008B501F] w-[137px] text-sm p-2 rounded-sm"
                    >
                      Approve
                    </button>
                    <button
                      type="button"
                      className="text-gray-500 bg-gray-300 w-[137px] text-sm p-2 rounded-sm"
                    >
                      Cancel
                    </button>
                  </div>
                </article>
              </section>
            </section>
          </>
        ) : (
          <div className="flex items-center justify-center h-64">
            <p>No restaurant details found.</p>
          </div>
        )}
      </section>
    </section>
  );
};

export default VendorDetails;
